import DateSelection from "@/components/controls/DateSelection";
import SingleInput from "@/components/controls/SingleInput";
import NoLicensesEmptyState from "@/components/controls/NoLicensesEmptyState";
import SingleSelect from "@/components/controls/SingleSelect";
import {
  FieldConfig,
  FieldItem,
  FieldsConfig
} from "@/components/layouts/DetailFields";
import Text from "@/components/ui/text";
import {
  calculateContractEndDate,
  contractEndDateFieldsForStartDate,
  dealContractDurations,
  determineDealContractDuration,
  getCurrencySymbol,
  monetaryValueFormatter,
  prettyNumberValueFormatter
} from "@/pages/deals/helpers";
import { Commercials, DealContractDuration, Period } from "@/pages/deals/types";
import {
  useCurrencies,
  useLicenseServiceItemsByProduct
} from "@/entities/settings/hooks";
import {
  calculateMonthsBetweenDates,
  simpleDate,
  toLocalDate,
  toDateOnlyISO
} from "@/utils/datetime";
import { getEnumTKey } from "@/utils/strings";
import { cn } from "@/utils/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import IconContainer from "@/components/ui/icon-container";
import { Add, Error, InfoCircle } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import AmountBreakdownModal from "./AmountBreakdownModal";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { CheckboxGroupItem } from "@/components/ui/checkbox-group";
import FileUpload from "@/components/controls/FileUpload/FileUpload";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import ExistingBreakdown from "./ExistingBreakdown";
import ConvertedCurrency from "@/pages/deals/components/commercials/ConvertedCurrency";
import { useFinancials } from "@/services/hooks/useFinancials";
import { forEach, get, isEmpty, keys, map } from "lodash";
import { ISOStringFormat } from "date-fns";

const TRANSLATION_PREFIX = "deals.commercials";

interface Props {
  data: Commercials | null;
  onUpdateField?: (
    field: string,
    newValue: string | number | Object | null,
    additionalFields?: { [fieldName: string]: string | number | null }
  ) => void;
  containerClassName?: string;
  isReadOnly?: boolean;
}

export function createGetFieldKey(data: Commercials | null) {
  return <K extends keyof Commercials>(fieldName: K) => {
    // Include productId in the key for licenseId field to force re-render when product changes
    const productSuffix =
      fieldName === "licenseId" ? `-${data?.productId ?? "no-product"}` : "";
    return `${String(fieldName)}-${data?.id}-${data?.[fieldName] ?? "empty"}${productSuffix}`;
  };
}

export const handleFieldError = (
  setFieldErrors: React.Dispatch<
    React.SetStateAction<{
      [fieldName: string]: string;
    }>
  >,
  fieldName: string,
  hasError: boolean,
  errorMessage?: string
) => {
  setFieldErrors((prev) => {
    const newFieldErrors = { ...prev };
    if (hasError && errorMessage) {
      newFieldErrors[fieldName] = errorMessage;
    } else {
      delete newFieldErrors[fieldName];
    }
    return newFieldErrors;
  });
};

export const getFieldValue = (data: Commercials | null, fieldName: string) => {
  const value = get(data, fieldName);
  return value;
};

const ContractTerms: React.FC<Props> = ({
  data,
  onUpdateField,
  containerClassName,
  isReadOnly = false
}) => {
  const { t } = useTranslation();
  const {
    accountCurrencySymbol,
    contractCurrencySymbol,
    rate,
    showConversion
  } = useFinancials(data);

  const currencies = useCurrencies().data ?? [];
  const [pendingFieldChange, setPendingFieldChange] = useState<{
    field: string;
    value: string | number | null;
    additionalFields?: { [fieldName: string]: string | number | null };
  } | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    [fieldName: string]: string;
  }>({});
  const [isAmountBreakdownModalOpen, setIsAmountBreakdownModalOpen] =
    useState(false);
  const [isDeleteBreakdownModalOpen, setIsDeleteBreakdownModalOpen] =
    useState(false);
  const [isChangeDurationModalOpen, setIsChangeDurationModalOpen] =
    useState(false);
  const { licenseItems: licenseTypes } = useLicenseServiceItemsByProduct(
    data?.productId
  );

  const getFieldKey = useCallback(createGetFieldKey(data), [data?.id]);
  const commercialsExist = data?.amountBreakdownByPeriod !== null;
  const isAmountBreakdownModalDisabled =
    !data?.contractStartDate || !data?.contractEndDate;

  useEffect(() => {
    const calculatedContractDuration = determineDealContractDuration(
      data?.contractStartDate,
      data?.contractEndDate
    );
    setDuration(calculatedContractDuration);
  }, [data?.contractStartDate, data?.contractEndDate]);

  useEffect(() => {
    // Truthy, not !isNull: the clear control writes "" rather than null, and
    // isNull("") is false — so a cleared field passed this guard and
    // toLocalDate("") substituted today, validating optOutDate against a date
    // the user had just removed. Line 137 above already tests these truthily.
    if (data?.contractStartDate && data?.contractEndDate && data?.optOutDate) {
      const startDate = toLocalDate(data?.contractStartDate);
      const endDate = toLocalDate(data?.contractEndDate);
      const optOutDate = toLocalDate(data?.optOutDate);

      let error = false;
      let errorMessage = "";
      if (optOutDate < startDate) {
        error = true;
        errorMessage = t(`${TRANSLATION_PREFIX}.disabledOptOutBefore`);
      } else if (optOutDate > endDate) {
        error = true;
        errorMessage = t(`${TRANSLATION_PREFIX}.disabledOptOutAfter`);
      }

      handleFieldError(setFieldErrors, "optOutDate", error, errorMessage);
    } else {
      // Clearing any of the three makes the guard above false. Without this the
      // last verdict stayed on screen — an out-of-range warning about a date
      // the user had just removed, which only cleared once all three were set
      // again.
      handleFieldError(setFieldErrors, "optOutDate", false, "");
    }
  }, [data?.contractStartDate, data?.contractEndDate, data?.optOutDate]);

  useEffect(() => {
    setFieldErrors({});
  }, [data?.id]);

  const handleEditBreakdown = () => {
    setIsAmountBreakdownModalOpen(true);
  };

  const handleDeleteBreakdown = () => {
    onUpdateField?.("amountBreakdownByPeriod", null);
    setIsDeleteBreakdownModalOpen(false);
  };

  const handleFileAttachments = (attachments: SavedAttachmentInfo[]) => {
    onUpdateField?.("uploadContracts", { contracts: attachments });
  };

  const applyPendingChanges = () => {
    if (!pendingFieldChange) return;

    const { field, value, additionalFields } = pendingFieldChange;

    const allAdditionalFields = {
      ...additionalFields,
      amountBreakdownByPeriod: null
    };

    onUpdateField?.(field, value, allAdditionalFields);

    if (field === "contractDuration") {
      setDuration(Number(value));
    }
    setIsChangeDurationModalOpen(false);
    setPendingFieldChange(null);
  };

  const cancelPendingChanges = () => {
    setIsChangeDurationModalOpen(false);
    setPendingFieldChange(null);
  };

  const initialAttachments = useMemo(
    () => data?.uploadContracts?.contracts || [],
    [JSON.stringify(data?.uploadContracts?.contracts)]
  );

  const fieldsConfig: FieldsConfig = {
    currency: {
      ...(isReadOnly
        ? {
            type: "readOnlyText",
            formatter: (value) => {
              return `${getCurrencySymbol(value)} ${value}`;
            }
          }
        : {
            type: "select",
            options: map(currencies, (currencyCode) => ({
              id: currencyCode,
              displayName: `${getCurrencySymbol(currencyCode)} ${currencyCode}`
            }))
          })
    },
    contractStartDate: {
      ...(isReadOnly
        ? {
            type: "readOnlyDate"
          }
        : {
            type: "date",
            onChange: (newDate: string) => {
              // One decision, shared by both paths below — see the helper for
              // why they must not answer this separately.
              const endDateFields = contractEndDateFieldsForStartDate(
                newDate as ISOStringFormat,
                duration
              );

              if (commercialsExist) {
                setPendingFieldChange({
                  field: "contractStartDate",
                  value: newDate,
                  additionalFields: endDateFields
                });
                setIsChangeDurationModalOpen(true);
              } else {
                onUpdateField?.("contractStartDate", newDate, endDateFields);
              }
            }
          })
    },
    contractDuration: {
      selectedValue: duration,
      formatter: (value: unknown) =>
        t(
          getEnumTKey(
            "deals",
            "contractDurations",
            DealContractDuration,
            value as number
          )
        ),
      ...(isReadOnly
        ? {
            type: "readOnlyText"
          }
        : {
            type: "select",
            onChange: (value: string | number) => {
              if (commercialsExist) {
                let endDateValue = null;
                if (value !== DealContractDuration.OTHER) {
                  endDateValue = calculateContractEndDate(
                    data?.contractStartDate,
                    value as DealContractDuration
                  );
                }
                setPendingFieldChange({
                  field: "contractDuration",
                  value: Number(value),
                  additionalFields: {
                    contractEndDate: endDateValue
                  }
                });
                setIsChangeDurationModalOpen(true);
              } else {
                setDuration(Number(value));
                if (value === DealContractDuration.OTHER) {
                  return onUpdateField?.("contractEndDate", null);
                }
                const newEndDate = calculateContractEndDate(
                  data?.contractStartDate,
                  value as DealContractDuration
                );
                onUpdateField?.("contractEndDate", newEndDate as string);
              }
            },
            options: map(dealContractDurations, (duration) => ({
              id: duration,
              name: DealContractDuration[duration]
            }))
          })
    },
    contractEndDate: {
      ...(isReadOnly
        ? { type: "readOnlyDate" }
        : {
            type: "date",
            // toLocalDate("") is today, which would silently bound the
            // calendar to today instead of leaving it unbounded.
            availableFrom: data?.contractStartDate
              ? toLocalDate(data.contractStartDate as string)
              : undefined,
            onChange: (newDate) => {
              if (commercialsExist) {
                setPendingFieldChange({
                  field: "contractEndDate",
                  value: newDate
                });
                setIsChangeDurationModalOpen(true);
              } else {
                setDuration(DealContractDuration.OTHER);
                onUpdateField?.("contractEndDate", newDate);
              }
            },
            // Truthy, not === null: the clear control writes "", so a cleared
            // start date left this picker enabled.
            disabled: !data || !data.contractStartDate,
            disabledTooltipText: t(`${TRANSLATION_PREFIX}.disabledEndDate`)
          })
    },
    optOutDate: {
      ...(isReadOnly
        ? { type: "readOnlyDate" }
        : {
            type: "date",
            disabled: !data || !data.contractStartDate || !data.contractEndDate,
            disabledTooltipText: t(`${TRANSLATION_PREFIX}.disabledOptOutDate`),
            // toLocalDate("") is today, which would silently bound the
            // calendar to today instead of leaving it unbounded.
            availableFrom: data?.contractStartDate
              ? toLocalDate(data.contractStartDate as string)
              : undefined,
            availableTo: data?.contractEndDate
              ? toLocalDate(data.contractEndDate as string)
              : undefined
          })
    },
    contractTotalAmount: {
      suffixComponent:
        showConversion &&
        data?.contractTotalAmount &&
        data?.contractTotalAmount > 0 ? (
          <ConvertedCurrency
            amount={data?.contractTotalAmount}
            rate={rate}
            accountCurrencySymbol={accountCurrencySymbol}
          />
        ) : undefined,
      infoTooltip: t(`${TRANSLATION_PREFIX}.contractTotalAmountTooltip`),
      followingComponent: commercialsExist ? (
        <ExistingBreakdown
          data={data}
          handleEditBreakdown={handleEditBreakdown}
          setIsDeleteBreakdownModalOpen={setIsDeleteBreakdownModalOpen}
          contractCurrencySymbol={contractCurrencySymbol}
          isReadOnly={isReadOnly}
        />
      ) : undefined,
      ...(isReadOnly || commercialsExist
        ? {
            type: "readOnlyText",
            formatter: (value) =>
              monetaryValueFormatter(value, contractCurrencySymbol)
          }
        : {
            type: "editableInput",
            isMonetary: true,
            maxDigits: 10,
            currencySymbol: contractCurrencySymbol
          })
    },
    licenseId: {
      ...(isReadOnly
        ? {
            type: "readOnlyText",
            formatter: (value) =>
              licenseTypes.find((license) => license.id === value)?.name || ""
          }
        : {
            type: "select",
            options: map(licenseTypes, (license) => ({
              id: license.id,
              displayName: license.name
            })),
            additionalProps: {
              popOverHeader:
                licenseTypes.length === 0 ? (
                  <NoLicensesEmptyState
                    labels={{
                      title: t("common.noLicensesTitle"),
                      description: t("common.noLicensesDescription")
                    }}
                  />
                ) : undefined
            }
          })
    },
    licensesInDeal: {
      ...(isReadOnly
        ? {
            type: "readOnlyText",
            formatter: (value) => prettyNumberValueFormatter(value)
          }
        : {
            type: "editableInput",
            isNumericOnly: true,
            isMonetary: false,
            isPretty: true
          })
    },
    potentialLicensesInCompany: {
      ...(isReadOnly
        ? {
            type: "readOnlyText",
            formatter: (value) => prettyNumberValueFormatter(value)
          }
        : {
            type: "editableInput",
            isNumericOnly: true,
            isMonetary: false,
            isPretty: true
          })
    },
    implementationCost: {
      suffixComponent:
        showConversion &&
        data?.implementationCost &&
        data?.implementationCost > 0 ? (
          <ConvertedCurrency
            amount={data?.implementationCost}
            rate={rate}
            accountCurrencySymbol={accountCurrencySymbol}
          />
        ) : undefined,
      ...(isReadOnly
        ? {
            type: "readOnlyText",
            formatter: (value) =>
              monetaryValueFormatter(value, contractCurrencySymbol)
          }
        : {
            type: "editableInput",
            isMonetary: true,
            currencySymbol: contractCurrencySymbol
          })
    },
    trainingCost: {
      suffixComponent:
        showConversion && data?.trainingCost && data?.trainingCost > 0 ? (
          <ConvertedCurrency
            amount={data?.trainingCost}
            rate={rate}
            accountCurrencySymbol={accountCurrencySymbol}
          />
        ) : undefined,
      ...(isReadOnly
        ? {
            type: "readOnlyText",
            formatter: (value) =>
              monetaryValueFormatter(value, contractCurrencySymbol)
          }
        : {
            type: "editableInput",
            isMonetary: true,
            currencySymbol: contractCurrencySymbol
          })
    }
  };

  const saveBreakdown = (periods: Period[]) => {
    onUpdateField?.("amountBreakdownByPeriod", { periods });
    setIsAmountBreakdownModalOpen(false);
  };

  const renderFieldComponent = (
    fieldName: keyof Commercials,
    config: FieldConfig
  ) => {
    const value = config?.selectedValue
      ? config?.selectedValue
      : getFieldValue(data, fieldName);

    switch (config.type) {
      case "select":
        return (
          <SingleSelect
            key={getFieldKey(fieldName)}
            fieldName={fieldName}
            options={config.options}
            selectedValue={value}
            onSelection={(newValue) => {
              let additionalFields = {};
              if (!isEmpty(config?.dependencies)) {
                forEach(config?.dependencies, (dependency) => {
                  additionalFields = {
                    ...additionalFields,
                    [dependency]: null
                  };
                });
              }
              onUpdateField?.(fieldName, newValue, additionalFields);
              config.onChange?.(newValue);
            }}
            formatter={config.formatter}
            renderIcon={config.renderOptionIcon}
            entity={config.entity}
            showSearch={config?.showSearch}
            fixedOptions={config?.fixedOptions}
            popoverClassName={config?.popoverClassName}
            treatNullAsValue={config?.treatNullAsValue}
            disabled={config?.disabled}
            disabledTooltipText={config?.disabledTooltipText}
            initialSearchTerm={config?.searchTerm}
            onSearch={config?.onSearch}
            isLoading={config?.isLoading}
            placeholder={
              config.label ? `${t("select")} ${config.label}` : t("select")
            }
            className="h-9 w-[75%]"
            {...config.additionalProps}
          />
        );

      case "editableInput":
        return (
          <SingleInput
            key={getFieldKey(fieldName)}
            fieldName={fieldName}
            initialValue={
              value?.toString() ?? config.initialValue?.toString() ?? ""
            }
            onSave={(newValue: string) => onUpdateField?.(fieldName, newValue)}
            textVariant="body"
            validation={config.validation}
            isMonetary={config.isMonetary}
            isPretty={config.isPretty}
            isNumericOnly={config?.isNumericOnly}
            maxDigits={config?.maxDigits}
            onError={(error) =>
              handleFieldError(setFieldErrors, fieldName, !!error)
            }
            currencySymbol={config?.currencySymbol}
            placeholder={
              config.label ? `${t("add")} ${config.label}` : t("add")
            }
            width="75%"
            {...config.additionalProps}
          />
        );

      case "date":
        return (
          <DateSelection
            key={getFieldKey(fieldName)}
            date={value ? toLocalDate(value) : null}
            onDateSelection={(newDate: Date) => {
              const dateStr = toDateOnlyISO(newDate);
              if (config?.onChange) {
                config.onChange(dateStr);
              } else {
                onUpdateField?.(fieldName, dateStr);
              }
            }}
            disabled={config?.disabled}
            disabledTooltipText={config?.disabledTooltipText}
            availableFrom={config?.availableFrom}
            availableTo={config?.availableTo}
            onClear={
              !config?.required
                ? () => {
                    if (config?.onChange) {
                      config.onChange("");
                    } else {
                      onUpdateField?.(fieldName, "");
                    }
                  }
                : undefined
            }
            {...config.additionalProps}
          />
        );

      case "readOnlyDate":
        return (
          <Text
            key={getFieldKey(fieldName)}
            variant="body"
            className="pl-1 px-3"
            data-test={fieldName}
          >
            {value ? simpleDate(toLocalDate(value), "long") : t("unknown")}
          </Text>
        );

      case "readOnlyText":
        return (
          <Text
            key={getFieldKey(fieldName)}
            variant="body"
            className="pl-1 px-3"
          >
            {config.formatter
              ? config.formatter(value)
              : (value ?? t("unknown"))}
          </Text>
        );

      default:
        return null;
    }
  };

  const topFieldNames = [
    "currency",
    "contractStartDate",
    "contractDuration",
    "contractEndDate",
    "optOutDate",
    "contractTotalAmount"
  ];
  const bottomFieldNames = [
    "licenseId",
    "licensesInDeal",
    "potentialLicensesInCompany",
    "implementationCost",
    "trainingCost"
  ];

  const getFieldItemsForGroup = (fieldGroup: string[]) => {
    return Object.fromEntries(
      Object.entries(fieldsConfig)
        .filter(([fieldName]) => fieldGroup.includes(fieldName))
        .map(([fieldName, config]) => [
          fieldName,
          {
            component: renderFieldComponent(
              fieldName as keyof Commercials,
              config
            ),
            icon: config.icon || ""
          }
        ])
    );
  };

  const renderField = (fieldName: string, fieldItem: FieldItem) => {
    const fieldConfig = fieldsConfig[fieldName];
    const hasError = fieldErrors[fieldName];

    return (
      <div key={fieldName} className="group contents">
        <div
          className={cn(
            "flex gap-2 pr-4 h-9",
            "overflow-hidden",
            hasError ? "items-start pt-2" : "items-center"
          )}
        >
          <Text
            variant="bodySecondary"
            className="truncate"
            hasTruncatedTooltip
          >
            {fieldConfig.label || t(`${TRANSLATION_PREFIX}.${fieldName}`)}
          </Text>
          {fieldConfig?.infoTooltip && (
            <Tooltip>
              <TooltipTrigger>
                <IconContainer color="secondary">
                  <InfoCircle />
                </IconContainer>
              </TooltipTrigger>
              <TooltipContent maxWidth={18}>
                {fieldConfig?.infoTooltip}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div
          className={cn(
            "flex break-words",
            hasError
              ? "h-16 items-center overflow-hidden"
              : "h-9 items-center overflow-hidden"
          )}
        >
          {fieldItem.component}
          {fieldConfig?.suffixComponent && fieldConfig.suffixComponent}
        </div>
      </div>
    );
  };

  const renderFieldsContainer = (
    fields: Record<string, FieldItem>,
    additionalClasses?: string
  ) => {
    return (
      <div
        className={cn(
          "grid grid-cols-[minmax(0,50%),1fr] gap-y-1",
          containerClassName,
          additionalClasses
        )}
      >
        {map(keys(fields), (fieldName) => (
          <React.Fragment key={fieldName}>
            {renderField(fieldName, fields[fieldName])}
            {fieldErrors[fieldName] && (
              <div className="col-span-2 pt-1 flex gap-1">
                <IconContainer color="error">
                  <Error />
                </IconContainer>
                <Text variant="smBodyError">{fieldErrors[fieldName]}</Text>
              </div>
            )}
            {fieldsConfig[fieldName]?.followingComponent}
          </React.Fragment>
        ))}
      </div>
    );
  };
  return (
    <>
      <div className="flex flex-col">
        {renderFieldsContainer(getFieldItemsForGroup(topFieldNames))}
        {!commercialsExist && !isReadOnly && (
          <Button
            variant="tertiary"
            className="w-fit flex gap-1 -translate-x-2"
            onClick={() => setIsAmountBreakdownModalOpen(true)}
            disabled={isAmountBreakdownModalDisabled}
            tooltipText={
              isAmountBreakdownModalDisabled
                ? t(`${TRANSLATION_PREFIX}.amountBreakdownDisabled`)
                : undefined
            }
          >
            <Add />
            <span>{t(`${TRANSLATION_PREFIX}.amountBreakdown`)}</span>
          </Button>
        )}
        <CheckboxGroupItem
          id="autoRenewal"
          label={t(`${TRANSLATION_PREFIX}.autoRenewal`)}
          checked={Boolean(data?.autoRenewal)}
          onCheckedChange={(checked: boolean) =>
            onUpdateField?.("autoRenewal", checked)
          }
          disabled={isReadOnly}
        />
        {!isReadOnly && <div className="h-[1px] my-4 bg-border-border1"></div>}
        {renderFieldsContainer(getFieldItemsForGroup(bottomFieldNames))}
        {!isReadOnly && <div className="h-[1px] my-4 bg-border-border1"></div>}
        <div className="flex flex-col gap-4">
          {isReadOnly ? (
            initialAttachments?.length > 0 ? (
              <Text variant="bodySecondary" className="mt-1">
                {t(`${TRANSLATION_PREFIX}.contract`)}
              </Text>
            ) : null
          ) : (
            <Text variant="bodySecondary">
              {t(`${TRANSLATION_PREFIX}.uploadContract`)}
            </Text>
          )}
          <FileUpload
            onAttachmentsChange={handleFileAttachments}
            initialAttachments={initialAttachments}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>
      <AmountBreakdownModal
        isOpen={isAmountBreakdownModalOpen}
        setIsOpen={setIsAmountBreakdownModalOpen}
        periodsBreakdown={
          data?.amountBreakdownByPeriod
            ? data?.amountBreakdownByPeriod?.periods
            : null
        }
        handleSubmit={saveBreakdown}
        showConversion={showConversion}
        contractCurrencySymbol={contractCurrencySymbol}
        totalAmount={data?.contractTotalAmount ?? 0}
        contractDurationInMonths={calculateMonthsBetweenDates(
          data?.contractStartDate,
          data?.contractEndDate
        )}
        rate={rate}
        accountCurrencySymbol={accountCurrencySymbol}
      />
      <ConfirmationModal
        isOpen={isDeleteBreakdownModalOpen}
        setIsOpen={setIsDeleteBreakdownModalOpen}
        title={t(`${TRANSLATION_PREFIX}.deleteConfirmationModal.title`)}
        confirmationText={t(
          `${TRANSLATION_PREFIX}.deleteConfirmationModal.confirmationText`
        )}
        handleSubmit={handleDeleteBreakdown}
      />
      <ConfirmationModal
        isOpen={isChangeDurationModalOpen}
        setIsOpen={setIsChangeDurationModalOpen}
        title={t(`${TRANSLATION_PREFIX}.changeDurationModal.title`)}
        confirmationText={t(
          `${TRANSLATION_PREFIX}.changeDurationModal.confirmationText`
        )}
        handleSubmit={applyPendingChanges}
        handleCancel={cancelPendingChanges}
        isPrimaryButton
        submitButtonText={t("confirm")}
      />
    </>
  );
};

export default ContractTerms;
